/**
 * Store for directive information
 *
 * TODO: (sam) Move all `should` methods to directive/index.js
 *
 * @module
 */

var _ = require('optimizely-client-fn');
var actionTypes = require('../constants/action_types');

/**
 * @typedef {Object} DirectiveState
 * @property {boolean} disabled - if the client is totally disabled (`initialize` wasn't called)
 * @property {string[]} forceAudienceIds - any optimizely_x_audiences Audiences specified in query param. Passing "", "none" or similarly
                                                                      nonexistent audience ids will opt you out of all audiences
 * @property {string[]} forceVariationIds - any optimizely_x Variations specified in query param. Passing "", "none" or similarly
                                                             nonexistent variation ids will opt you out of all variations
 * @property {boolean} isEditor - whether operating in editor mode
 * @property {boolean} alreadyInitialized - whether a client has fired its 'initialized' lifecycle event on the page
 * @property {boolean} isPreview - whether operating in preview mode
 * @property {boolean} isSlave - whether operating in slave mode
 * @property {string} projectToken - value for `__TOKEN__` parameter in preview URL template
 * @property {boolean} shouldOptOut - whether the visitor should be opted out
 * @property {boolean} trackingDisabled - whether tracking is disabled
 * @property {boolean} forceTracking - whether to send tracking data even in preview and with force variations/audiences
 */

/**
 * Holds all directive information
 */
module.exports = {
  initialize: function() {
    this.__state = {
      disabled: false,
      forceAudienceIds: [],
      forceVariationIds: [],
      alreadyInitialized: false,
      mutationObserverAPISupported: false,
      isEditor: false,
      isPreview: false,
      isLegacyPreview: false,
      isSlave: false,
      previewLayerIds: [],
      projectToken: null,
      shouldOptOut: false,
      trackingDisabled: false,
      isRunningInV2Editor: false,
      isRunningInDesktopApp: false,
      forceTracking: false,
      requestedEmbeddedEditor: false,
    };

    this.on(actionTypes.LOAD_DIRECTIVE, this.__loadDirective);
  },

  /**
   *
   * @returns {DirectiveState}
   */
  getAll: function(): import('core/stores/directive').DirectiveState {
    return _.cloneDeep(this.__state);
  },

  /**
   * Whether or not there is a fatal conflict in observing changes instructions.
   * If observe indefinitely feature is enabled but the broswer doesn't support
   * the MutationObserver API, the client must abort.
   */
  conflictInObservingChanges: function() {
    return __OBSERVE_CHANGES_INDEFINITELY__ &&
           !this.__state.mutationObserverAPISupported;
  },

  /**
   * If any disable query params are present
   * @returns {boolean}
   */
  isDisabled: function(): boolean {
    return this.__state.disabled;
  },

  /**
   * If running in editor mode.
   * @returns {boolean}
   */
  isEditor: function(): boolean {
    return this.__state.isEditor;
  },

  /**
   * If a client has fired its 'initialized' lifecycle event on this page
   * @returns {boolean}
   */
  clientHasAlreadyInitialized: function(): boolean {
    return this.__state.alreadyInitialized;
  },

  /**
   * Retrieves all audience ids we want to force user into
   * @return {(string[]|null)}
   */
  getForceAudienceIds: function(): string[]|null {
    return this.__state.forceAudienceIds;
  },

  /**
   * Retrieves all variation ids that we want to force the user into
   * @return {string[]}
   */
  getForceVariationIds: function(): string[] {
    return this.__state.forceVariationIds;
  },

  /**
   * Retrieves all campaign ids that we want to fetch working copy data for.
   * @return {string[]}
   */
  getPreviewLayerIds: function(): string[] {
    return this.__state.previewLayerIds;
  },

  /**
   * Get project token
   * @returns {string|null}
   */
  getProjectToken: function(): string|null {
    return this.__state.projectToken;
  },

  /**
   * Get whether forceTracking is set
   * @returns {boolean}
   */
  getForceTracking: function(): boolean {
    return this.__state.forceTracking;
  },

  /**
   * Whether the client should activate based on store state
   * @returns {boolean}
   */
  shouldActivate: function(): boolean {
    return !this.__state.isEditor && !this.isDisabled();
  },

  /**
   * Whether we should bootstrap data for preview
   * @returns {boolean}
   */
  shouldBootstrapDataForPreview: function(): boolean {
    return this.__state.isPreview;
  },

  /**
   * Whether we should bootstrap data for editor build
   * @returns {boolean}
   */
  shouldBootstrapDataForEditor: function(): boolean {
    return this.__state.isEditor;
  },

  /**
   * Whether the client should initialize based on store state: is preview, is disabled
   * @returns {boolean}
   */
  shouldInitialize: function(): boolean {
    return !(this.shouldLoadPreview() ||
           this.isDisabled() ||
           !!this.getProjectToken());
  },

  /**
   * Whether preview should be loaded
   * @returns {boolean}
   */
  shouldLoadPreview: function(): boolean {
    return !this.__state.isPreview &&
           !this.__state.isLegacyPreview &&
           !!this.getProjectToken() &&
           !this.__state.isEditor;
  },

  /**
   * Whether the editor mfe should be loaded
   * @returns {boolean}
   */
  shouldLoadEmbeddedEditor: function (): boolean {
    return this.__state.requestedEmbeddedEditor && !this.__state.isEditor;
  },

  /**
   * Whether we should no op because were in the desktop app.
   * @returns {boolean}
   */
  shouldBailForDesktopApp: function(): boolean {
    return !this.__state.isEditor &&
           this.__state.isRunningInDesktopApp;
  },

  /**
   * Whether innie should be loaded
   * @returns {boolean}
   */
  shouldLoadInnie: function(): boolean {
    return !this.__state.isSlave &&
           !this.__state.isEditor &&
           this.__state.isRunningInV2Editor;
  },

  /**
   * Whether or not change plugins should observe selectors
   * indefinitely. As of now, this can only happen if the feature
   * is enabled and the MutationObserver API is supported by the browser.
   * @returns {boolean}
   */
  shouldObserveChangesIndefinitely: function(): boolean {
    return __OBSERVE_CHANGES_INDEFINITELY__ &&
           this.__state.mutationObserverAPISupported;
  },

  /**
   * The opposite of DirectiveStore.shouldObserveChangesIndefinitely.
   * @returns {*|boolean}
   */
  shouldObserveChangesUntilTimeout: function(): any|boolean {
    return !this.shouldObserveChangesIndefinitely();
  },

  /**
   * Whether the visitor should be opted out
   * @returns {boolean}
   */
  shouldOptOut: function(): boolean {
    return this.__state.shouldOptOut;
  },

  /**
   * Whether tracking data should be sent
   * @returns {boolean}
   */
  shouldSendTrackingData: function(): boolean {
    if (this.__state.trackingDisabled) {
      return false;
    }
    if (this.__state.forceTracking) {
      return true;
    }
    return !this.__state.isPreview &&
            _.isEmpty(this.getForceVariationIds()) &&
            _.isEmpty(this.getForceAudienceIds());
  },

  /**
   * Whether running in slave mode
   * @returns {boolean}
   */
  isSlave: function(): boolean {
    return this.__state.isSlave;
  },

  /**
   * Whether running in the desktop app.
   * @returns {boolean}
   */
  isRunningInDesktopApp: function(): boolean {
    return this.__state.isRunningInDesktopApp;
  },

  /**
   * Whether running in the v2 editor
   * @returns {boolean}
   */
  isRunningInV2Editor: function(): boolean {
    return this.__state.isRunningInV2Editor;
  },

  /**
   * Sets directive data
   * @param {Object} payload
   * @param {boolean} [payload.disabled]
   * @param {boolean} [payload.mutationObserverAPISupported]
   * @param {string[]} [payload.forceAudienceIds]
   * @param {string[]} [payload.forceVariationIds]
   * @param {boolean} [payload.isEditor]
   * @param {boolean} [payload.isPreview]
   * @param {boolean} [payload.isSlave]
   * @param {string} [payload.projectToken]
   * @param {boolean} [payload.shouldOptOut]
   * @private
   */
  __loadDirective: function(payload:any) {
    _.extend(this.__state, payload);
    this.emitChange();
  },
};
